import React, { useEffect, useRef } from "react";
import { ChatMessage } from "../../types/chatbot";
import MessageBubble from "./MessageBubble";
import ChatComposer from "./ChatComposer";

interface ChatWindowProps {
  messages: ChatMessage[];
  pending: boolean;
  error: string | null;
  emptyHint?: string;
  onSend: (text: string) => void;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  messages,
  pending,
  error,
  emptyHint,
  onSend,
}) => {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const frame = window.requestAnimationFrame(() => {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: "smooth",
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [messages, pending]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) < Math.abs(e.deltaX)) return;

      const maxScroll = el.scrollHeight - el.clientHeight;
      if (maxScroll <= 0) return;

      e.preventDefault();
      e.stopPropagation();
      const delta =
        e.deltaMode === WheelEvent.DOM_DELTA_LINE
          ? e.deltaY * 36
          : e.deltaMode === WheelEvent.DOM_DELTA_PAGE
            ? e.deltaY * el.clientHeight
            : e.deltaY * 5;

      el.scrollTop = Math.max(0, Math.min(el.scrollTop + delta, maxScroll));
    };

    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, []);

  // The last assistant message is the one currently streaming when pending=true.
  const lastAssistantIndex = (() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === "assistant") return i;
    }
    return -1;
  })();

  return (
    <div className="chatbot-window">
      <div className="chatbot-window__messages" ref={listRef}>
        {messages.length === 0 && !pending && (
          <p className="chatbot-window__empty">
            {emptyHint ?? "Start the conversation by typing a question below."}
          </p>
        )}
        {messages.map((m, i) => (
          <MessageBubble
            key={m.id}
            message={m}
            streaming={pending && i === lastAssistantIndex}
          />
        ))}
        {/* Typing indicator only when we don't yet have a streaming placeholder */}
        {pending && lastAssistantIndex === -1 && (
          <div className="chatbot-msg chatbot-msg--assistant">
            <div className="chatbot-msg__avatar" aria-hidden>
              <i className="fas fa-robot"></i>
            </div>
            <div className="chatbot-msg__body">
              <div className="chatbot-msg__bubble chatbot-msg__bubble--typing">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        {error && (
          <p className="chatbot-window__error text-danger">{error}</p>
        )}
      </div>
      <ChatComposer onSend={onSend} disabled={pending} />
    </div>
  );
};

export default ChatWindow;
